/**
 * API Service
 *
 * Centralized service for making authenticated API calls to the backend.
 * Automatically includes the Auth0 access token in requests.
 *
 * Usage:
 * ```typescript
 * import { apiClient, publicApiClient } from '@/services/api';
 *
 * // Authenticated GET request
 * const data = await apiClient.get('/api/v1/endpoint');
 *
 * // Public GET request (no auth headers)
 * const publicData = await publicApiClient.get('/api/v1/public/endpoint');
 * ```
 */

import { ApiError, handleApiError } from "./apiErrorHandler";
import { getUiSandboxResponse } from "@/dev/uiSandbox";

// Re-export ApiError for backwards compatibility
export { ApiError };

// Base URL for your backend API
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000";

/**
 * Token getter function type.
 * This will be set by the useApi hook.
 */
let getToken: ((options?: { forceRefresh?: boolean }) => Promise<string>) | null = null;

/**
 * Logout handler function type.
 * This will be set by the useApi hook to trigger logout on persistent auth errors.
 */
let onAuthError: ((message?: string) => void) | null = null;

export const SIGNUP_DENIED_MESSAGE =
  "Your organization must be approved before you can sign up. Contact us to get access.";

/**
 * Set the token getter function.
 * Called by useApi hook to provide Auth0 token access.
 *
 * @param tokenGetter - Function that returns a Promise resolving to the access token
 */
export function setTokenGetter(tokenGetter: (options?: { forceRefresh?: boolean }) => Promise<string>) {
  getToken = tokenGetter;
}

/**
 * Set the auth error handler (logout function).
 * Called by useApi hook to handle persistent auth errors.
 *
 * @param handler - Function to call when authentication fails
 */
export function setAuthErrorHandler(handler: (message?: string) => void) {
  onAuthError = handler;
}

/**
 * Clear the token getter when the user logs out or auth context changes.
 */
export function clearTokenGetter() {
  getToken = null;
}

/**
 * Clear the auth error handler.
 */
export function clearAuthErrorHandler() {
  onAuthError = null;
}

/**
 * Check if the token getter is currently set.
 * Used to verify initialization state.
 *
 * @returns True if the token getter has been initialized
 */
export function isTokenGetterSet(): boolean {
  return getToken !== null;
}

/**
 * Make an authenticated API request with automatic token refresh on 401.
 *
 * @param url - The API endpoint URL (relative to API_BASE_URL)
 * @param options - Fetch request options
 * @param retryCount - Number of retry attempts (used internally)
 * @returns The fetch Response object
 * @throws ApiError if the request fails
 */
async function authenticatedFetch(
  url: string,
  options: RequestInit = {},
  retryCount = 0
): Promise<Response> {
  const sandboxResponse = getUiSandboxResponse(url, options);
  if (sandboxResponse) {
    return sandboxResponse;
  }

  const headers = new Headers(options.headers);

  // Fail fast if token getter is not set - this indicates a race condition
  if (!getToken) {
    const errorMsg =
      "Token getter not initialized. Ensure useApi() hook is called in a component wrapped by ProtectedRoute.";
    console.error(errorMsg);
    throw new Error("Not authenticated");
  }

  // Add Auth0 token
  try {
    const token = await getToken();
    if (!token) {
      throw new Error("Token getter returned empty token");
    }
    headers.set("Authorization", `Bearer ${token}`);
  } catch (error) {
    console.error("Failed to get access token:", error);
    if (onAuthError) {
      console.log("Triggering logout due to token getter failure");
      onAuthError();
    }
    throw new Error("Authentication failed");
  }

  // Set content type if not already set and body is present
  if (options.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  // Make the request
  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers,
  });

  if (response.status === 401 && retryCount === 0 && getToken) {
    console.log("Got 401, attempting to refresh token and retry...");
    try {
      const newToken = await getToken({ forceRefresh: true });
      if (newToken) {
        headers.set("Authorization", `Bearer ${newToken}`);
        return authenticatedFetch(
          url,
          {
            ...options,
            headers,
          },
          retryCount + 1
        );
      }
    } catch (error) {
      console.error(
        "Failed to refresh access token: Unable to obtain new authentication credentials",
        error
      );
    }
  }

  // Handle errors
  if (!response.ok) {
    if (response.status === 401 && onAuthError) {
      onAuthError();
    }
    if (response.status === 403 && onAuthError) {
      const errorData = await response.clone().json().catch(() => ({}));
      const detail = errorData.detail;
      if (typeof detail === "string" && detail.includes("organization must be approved")) {
        onAuthError(SIGNUP_DENIED_MESSAGE);
        throw new ApiError(SIGNUP_DENIED_MESSAGE, 403);
      }
    }
    await handleApiError(response);
  }

  return response;
}

/**
 * Make a public API request without authentication headers.
 * Use this for public endpoints that should not include auth tokens.
 *
 * @param url - The API endpoint URL (relative to API_BASE_URL)
 * @param options - Fetch request options
 * @returns The fetch Response object
 * @throws ApiError if the request fails
 */
async function publicFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const sandboxResponse = getUiSandboxResponse(url, options);
  if (sandboxResponse) {
    return sandboxResponse;
  }

  const headers = new Headers(options.headers);

  // Set content type if not already set and body is present
  if (options.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  // Make the request without auth headers
  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers,
  });

  // Handle errors
  if (!response.ok) {
    await handleApiError(response);
  }

  return response;
}

/**
 * API Client with common HTTP methods.
 * All methods include automatic authentication.
 */
export const apiClient = {
  /**
   * Make an authenticated GET request.
   *
   * @param url - The API endpoint URL
   * @param options - Optional fetch request options
   * @returns The parsed JSON response
   */
  async get<T = unknown>(url: string, options?: RequestInit): Promise<T> {
    const response = await authenticatedFetch(url, {
      ...options,
      method: "GET",
    });
    return response.json();
  },

  /**
   * Make an authenticated POST request.
   *
   * @param url - The API endpoint URL
   * @param data - The request body data (will be JSON stringified)
   * @param options - Optional fetch request options
   * @returns The parsed JSON response
   */
  async post<T = unknown>(url: string, data?: unknown, options?: RequestInit): Promise<T> {
    const response = await authenticatedFetch(url, {
      ...options,
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    });
    return response.json();
  },

  /**
   * Make an authenticated PUT request.
   *
   * @param url - The API endpoint URL
   * @param data - The request body data (will be JSON stringified)
   * @param options - Optional fetch request options
   * @returns The parsed JSON response
   */
  async put<T = unknown>(url: string, data?: unknown, options?: RequestInit): Promise<T> {
    const response = await authenticatedFetch(url, {
      ...options,
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
    });
    return response.json();
  },

  /**
   * Make an authenticated PATCH request.
   *
   * @param url - The API endpoint URL
   * @param data - The request body data (will be JSON stringified)
   * @param options - Optional fetch request options
   * @returns The parsed JSON response
   */
  async patch<T = unknown>(url: string, data?: unknown, options?: RequestInit): Promise<T> {
    const response = await authenticatedFetch(url, {
      ...options,
      method: "PATCH",
      body: data ? JSON.stringify(data) : undefined,
    });
    return response.json();
  },

  /**
   * Make an authenticated DELETE request.
   * Handles 204 No Content responses properly (no body to parse).
   *
   * @param url - The API endpoint URL
   * @param options - Optional fetch request options
   * @returns The parsed JSON response, or undefined for 204 responses
   */
  async delete<T = void>(url: string, options?: RequestInit): Promise<T> {
    const response = await authenticatedFetch(url, {
      ...options,
      method: "DELETE",
    });
    // 204 No Content has no body - return undefined
    if (response.status === 204) {
      return undefined as T;
    }
    return response.json();
  },
};

/**
 * Public API Client - for endpoints that don't require authentication.
 * Use this for public share access and other unauthenticated endpoints.
 */
export const publicApiClient = {
  /**
   * Make a public GET request (no authentication).
   *
   * @param url - The API endpoint URL
   * @param options - Optional fetch request options
   * @returns The parsed JSON response
   */
  async get<T = unknown>(url: string, options?: RequestInit): Promise<T> {
    const response = await publicFetch(url, {
      ...options,
      method: "GET",
    });
    return response.json();
  },

  /**
   * Make a public POST request (no authentication).
   *
   * @param url - The API endpoint URL
   * @param data - The request body data (will be JSON stringified)
   * @param options - Optional fetch request options
   * @returns The parsed JSON response
   */
  async post<T = unknown>(url: string, data?: unknown, options?: RequestInit): Promise<T> {
    const response = await publicFetch(url, {
      ...options,
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    });
    return response.json();
  },
};
